import { PriceCompareMessage } from '../../../../types/PriceCompareMessage';
import { ReachInformationCategory } from '../../../../types/ReachInformationMessage';
import { Text } from '../../../cdl/Text/Text';
import { Tooltip } from '../../../cdl/Tooltip/Tooltip';
import { translate } from '../../../common/helpers/translate.helper';
import { useProductStringFormat } from '../../../common/hooks/useProductStringFormat';
import { Box } from '../../../common/ui/Box';

import { Category } from './components/Category';
import { CheapestTag } from './components/CheapestTag';
import { DaysLeftSailing } from './components/DaysLeftSailing';
import { FormattedPrice } from './components/FormattedPrice';
import { PriceDifference } from './components/PriceDifference';

interface PriceCompareCategoryProps {
    priceData: PriceCompareMessage[];
    reachInformation?: ReachInformationCategory;
    isColumnHovered?: boolean;
}

export const PriceCompareCategory = ({
    priceData,
    reachInformation,
    isColumnHovered = false,
}: PriceCompareCategoryProps) => {
    const formatProductString = useProductStringFormat();

    const findSpecificPriceData = (key: keyof PriceCompareMessage) => {
        return priceData.find((priceData) => priceData[key] === true);
    };

    const defaultSupplier = findSpecificPriceData('defaultSupplier');
    const bestAlternativeData = findSpecificPriceData('bestAlternative');
    const isLowestPrice = !!findSpecificPriceData('bestPriceCategory');
    const isBestAlternativeLowest = !!bestAlternativeData?.bestPriceCategory;
    const showBestAlternative = !!bestAlternativeData && (isBestAlternativeLowest || isColumnHovered);

    return (
        <Category isCheapest={isLowestPrice}>
            <Box display="flex" justifyContent="space-between" alignItems="baseline">
                <Box>
                    {reachInformation !== undefined ? <DaysLeftSailing reachInformation={reachInformation} /> : null}
                </Box>
                {isLowestPrice ? <CheapestTag>{translate('schedule.priceCompare.lowestPrice')}</CheapestTag> : null}
            </Box>

            <Box display="flex" flexDirection="row" alignItems="flex-end" gap={16}>
                <Box display="flex" flexDirection="column">
                    <Text variant="extraSmall" color="foreground.subtle" as="p" style={{ whiteSpace: 'nowrap' }}>
                        {translate('schedule.priceCompare.defaultSupplier')}
                    </Text>

                    {defaultSupplier ? (
                        <Tooltip label={formatProductString(defaultSupplier.product)}>
                            <span>
                                <FormattedPrice
                                    price={defaultSupplier.price}
                                    positive={defaultSupplier.bestPriceCategory}
                                />
                            </span>
                        </Tooltip>
                    ) : (
                        <FormattedPrice price={undefined} positive={undefined} />
                    )}
                </Box>

                {showBestAlternative ? (
                    <>
                        <Box width="100%" display="flex" justifyContent="center">
                            {defaultSupplier?.bestAlternativeDifference ? (
                                <PriceDifference priceDifference={defaultSupplier.bestAlternativeDifference} />
                            ) : null}
                        </Box>

                        <Box display="flex" flexDirection="column">
                            <Text
                                variant="extraSmall"
                                color="foreground.subtle"
                                as="p"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                {translate('schedule.priceCompare.bestAlternative')}
                            </Text>

                            <Tooltip label={formatProductString(bestAlternativeData.product)}>
                                <span>
                                    <FormattedPrice
                                        price={bestAlternativeData.price}
                                        positive={bestAlternativeData.bestPriceCategory}
                                    />
                                </span>
                            </Tooltip>
                        </Box>
                    </>
                ) : null}
            </Box>
        </Category>
    );
};
